import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../utils/api';

const GRACE_DAYS = 5; // Display fallback only; server computes real state

interface MeResponse {
  id?: string;
  subscriptionStatus?: string | null;
  pastDueSince?: string | null;
  suspended?: boolean;
  graceDaysLeft?: number | null;
}

// Payment-failure guard for ADMIN accounts, rendered by Layout around page
// content. Fetches fresh status from the server because the localStorage
// user copy can be stale.
//
// - past_due within the grace period: dismissable warning banner with the
//   days remaining before suspension.
// - past_due beyond the grace period: account is suspended — replaces page
//   content with a blocking screen (except on the /subscription billing
//   pages so the customer can fix their payment method).
const PaymentFailedBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [dismissKey, setDismissKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<MeResponse>('/api/auth/me')
      .then((res) => {
        if (cancelled) return;
        const key = `paymentFailedBannerDismissed:${res.data?.id || 'unknown'}`;
        if (res.data?.subscriptionStatus !== 'past_due') {
          // Payment repaired (or never failed): reset dismissal so a
          // future failure in this session shows the banner again.
          sessionStorage.removeItem(key);
        }
        setDismissKey(key);
        setDismissed(sessionStorage.getItem(key) === 'true');
        setMe(res.data);
      })
      .catch(() => {
        // Never block the app over a status check failure.
      });
    return () => {
      cancelled = true;
    };
    // Refetch on navigation so long-lived sessions pick up new failures
    // and advancing countdowns without a full reload.
  }, [location.pathname]);

  // Trust the server's computed state — never re-derive suspension locally.
  const pastDue = me?.subscriptionStatus === 'past_due';
  const suspended = me?.suspended === true;
  const daysLeft = me?.graceDaysLeft ?? 0;

  // Suspended: block the whole app except the billing pages.
  const onBillingPage = location.pathname.startsWith('/subscription');
  if (suspended && !onBillingPage) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-24 px-6">
        <div className="max-w-md rounded-2xl border border-red-500/40 bg-red-500/10 p-8">
          <h1 className="text-xl font-bold text-red-200 mb-3">Account suspended</h1>
          <p className="text-sm text-red-200/90 mb-6">
            Your subscription payment failed more than {GRACE_DAYS} days ago, so
            your account is paused — including rent collection from your
            residents. Update your payment method to restore access immediately.
          </p>
          <Link
            to="/subscription"
            className="inline-block rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-semibold px-5 py-2.5"
          >
            Update payment method
          </Link>
        </div>
      </div>
    );
  }

  const showBanner = pastDue && !suspended && !dismissed;

  return (
    <>
      {showBanner && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
        >
          <p className="text-sm text-red-200">
            <span className="font-semibold">Payment failed.</span> Your last
            subscription payment didn't go through.{' '}
            {daysLeft > 0 ? (
              <>
                Update your payment method within{' '}
                <span className="font-semibold">
                  {daysLeft} day{daysLeft === 1 ? '' : 's'}
                </span>{' '}
                or your account (including rent collection) will be paused.
              </>
            ) : (
              <>Update your payment method now to avoid your account being paused.</>
            )}
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/subscription"
              className="text-sm font-semibold text-red-300 hover:text-red-100 underline"
            >
              Update payment method
            </Link>
            <button
              onClick={() => {
                if (dismissKey) sessionStorage.setItem(dismissKey, 'true');
                setDismissed(true);
              }}
              aria-label="Dismiss payment failed warning"
              className="text-red-300 hover:text-red-100 text-lg leading-none px-1"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {children}
    </>
  );
};

export default PaymentFailedBanner;
